import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect, useState } from 'react';
import ReactDom from 'react-dom';
import { useForm } from 'react-hook-form';

import { useStopwatch } from 'react-timer-hook';
import * as yup from 'yup';

import { Icon as BlueIcon } from '@blueprintjs/core';

import BackIconImage from '@assets/svg/back-icon.svg';
import FileIcon from '@assets/svg/file-document.svg';

import { useMutation, useQueryClient } from 'react-query';
import { fileUpload } from '@services/queries/onboarding/fileUpload';
import { NavigationContext } from '@layout/navigation/navigation';
import { useHistory } from 'react-router-dom';
import { runImporter } from '@services/queries/onboarding/runImporter';
import { importFile } from '@services/mixpanel/trackers/Onboarding';
import { IOnboardingFile } from '@models/Onboarding.model';

import { TemplateDownloadDialog } from '@components';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Back,
  Confirm,
  Row,
  Label,
  Line,
  Input,
  SelectFile,
  Form,
  Divider,
  SpacingDivider,
  ProgressContainer,
  FileImage,
  HolderDiv,
  TimerText,
  FileNameText,
  DescriptionText,
  TemplateDownloadSection,
  DownloadLink,
} from './ImportInventory.styles';

interface IImportInventory {
  open: boolean;
  onClose: () => void;
  files: IOnboardingFile[];
}

interface IFormData {
  file?: FileList;
}

interface IError {
  response: {
    data: {
      message: string;
    };
  };
}

export const ImportInventory = ({ open, onClose, files }: IImportInventory) => {
  const portalDiv = document.getElementById('portal');

  const [isFileNameExisting, setIsFileNameExisting] = useState<boolean>(false);

  const history = useHistory();

  const { studioId, studioName, userId, userEmail, userPicture, userName } =
    useContext(NavigationContext);

  const queryClient = useQueryClient();

  const [topic, setTopic] = useState<'user' | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [labelText, setLabelText] = useState<string>('Importing');
  const [descriptionText, setDescriptionText] = useState<string>(
    'Keep this window open to see the results.',
  );
  const [newFileId, setNewFileId] = useState<string>('');
  const [errorFile, setErrorFile] = useState<string>('');

  const [progress, setProgress] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [showTemplateDialog, setShowTemplateDialog] = useState<boolean>(false);

  const { seconds, minutes, start, pause } = useStopwatch({
    autoStart: false,
  });

  const checkFileNameInFiles = files.filter(
    file => file.originalname === fileName && file.status !== 'deleted',
  );

  const schema = yup
    .object({
      file: yup.mixed().required(),
    })
    .required();

  const mutationFileUpload = useMutation(
    async (result: any) => {
      const fileResponse = await fileUpload(result);

      const importerResponse = await runImporter({
        fileId: fileResponse.fileId,
        studioId,
      });

      return { fileResponse, importerResponse };
    },
    {
      onSuccess: async ({ fileResponse, importerResponse }) => {
        await queryClient.invalidateQueries(['onboarding', 'files']);

        queryClient.invalidateQueries(['inventory', 'equipments', 'pages']);

        importFile(studioName, 'spreadsheet', fileType, true);

        setNewFileId(fileResponse.fileId);

        setLabelText('Success');
        setDescriptionText('All assets imported!');
        setDisabled(false);
        pause();
        onClose();
        importerResponse.status === 200 && history.push('/products');
      },
      onError: ({ response }: IError) => {
        importFile(studioName, 'spreadsheet', fileType, false);

        setLabelText('Error');

        !checkFileNameInFiles.length &&
          setDescriptionText(response.data.message);

        pause();
        setDisabled(false);
      },
    },
  );

  const { register, handleSubmit } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const done = () => {
    history.push(`/onboarding/${newFileId}`);
    onClose();
  };

  useEffect(() => {
    if (checkFileNameInFiles.length) {
      setIsFileNameExisting(true);
    }
  }, [checkFileNameInFiles]);

  const onSubmit = (data: IFormData) => {
    setProgress(true);
    start();

    if (isFileNameExisting) {
      importFile(studioName, 'spreadsheet', fileType, false);

      setLabelText('Error');

      setDescriptionText('File already exists');

      pause();
      setDisabled(false);
    }
    if (!mutationFileUpload.isLoading && !isFileNameExisting) {
      const result: any = {
        file: data.file?.[0],
        triggeredById: userId,
        triggeredByAvatar: userPicture,
        triggeredBy: userName,
        triggeredByEmail: userEmail,
        studioId,
        studioName,
      };

      mutationFileUpload.mutate(result);
    }
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              {topic && (
                <>
                  <Back
                    src={BackIconImage}
                    alt="back"
                    onClick={() => setTopic(null)}
                  />
                  <SpacingDivider />
                </>
              )}
              <Title>Import Spreadsheet</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            {progress ? (
              <ProgressContainer>
                <Label status={labelText}>{labelText}</Label>

                <Divider height="8px" />

                <Row className="progressRow">
                  <HolderDiv>
                    <FileImage src={FileIcon} alt="FileIcon" />
                    <FileNameText className="file-name" limitOfLines={1}>
                      {fileName}
                    </FileNameText>
                  </HolderDiv>
                  <TimerText>{`${minutes < 10 ? `0${minutes}` : minutes}:${
                    seconds < 10 ? `0${seconds}` : seconds
                  }`}</TimerText>
                </Row>

                <Divider height="24px" />

                <DescriptionText>{descriptionText}</DescriptionText>

                <Line>
                  <Confirm
                    disabled={disabled || labelText === 'Error'}
                    onClick={done}
                  >
                    Import
                  </Confirm>
                </Line>
              </ProgressContainer>
            ) : (
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Label>File</Label>
                <Row className="pictureInputsWithLegend">
                  <Input
                    type="text"
                    placeholder="Select a .csv or .xlsx file"
                    value={fileName}
                    readOnly
                    isError={!!errorFile}
                  />

                  <SelectFile htmlFor="upload" isError={!!errorFile}>
                    Select File
                  </SelectFile>
                  <input
                    type="file"
                    id="upload"
                    accept=".csv, .xlsx"
                    hidden
                    {...register('file')}
                    onChange={e => {
                      const newFile = e.target.files?.[0];

                      if (newFile) {
                        register('file').onChange(e);
                        setFileName(e.target.files?.[0]?.name || '');
                        setFileType(newFile.type);

                        if (
                          ![
                            'text/csv',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          ].includes(newFile.type)
                        ) {
                          setErrorFile('Please select a .csv or .xlsx file.');
                        } else if (newFile.size >= 5242880) {
                          setErrorFile('Please select a file under 5MB.');
                        } else {
                          setErrorFile('');
                        }
                      }
                    }}
                  />
                </Row>

                {errorFile ? (
                  <DescriptionText status="Error">{errorFile}</DescriptionText>
                ) : (
                  <TemplateDownloadSection>
                    Unsure where to start?{' '}
                    <DownloadLink
                      onClick={() => {
                        setShowTemplateDialog(true);
                      }}
                      type="button"
                    >
                      Download our template
                    </DownloadLink>
                  </TemplateDownloadSection>
                )}

                <Line>
                  <Confirm type="submit" disabled={!fileName || !!errorFile}>
                    Import
                  </Confirm>
                </Line>
              </Form>
            )}

            <TemplateDownloadDialog
              open={showTemplateDialog}
              onClose={() => setShowTemplateDialog(false)}
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
